import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TenantService } from '../tenant/tenant.service';

export interface CompanyUserRef {
  id: string;
  name: string;
  email: string;
  /** URL de foto quando a API enviar (opcional). */
  avatarUrl?: string | null;
}

@Injectable({ providedIn: 'root' })
export class CompanyUsersService {
  private readonly http = inject(HttpClient);
  private readonly tenant = inject(TenantService);

  private readonly usersSignal = signal<CompanyUserRef[]>([]);
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);
  /** Empresa para a qual `users` / último erro se aplicam (após tentativa de load). */
  private readonly loadedCompanyIdSignal = signal<string | null>(null);
  private inflight: Promise<void> | null = null;

  readonly users = this.usersSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  readonly hasDataForActiveCompany = computed(() => {
    const cid = this.tenant.companyId();
    return !!cid && this.loadedCompanyIdSignal() === cid && !this.errorSignal();
  });

  /** Garante lista carregada para a empresa ativa; reutiliza cache por empresa na sessão. */
  async ensureLoaded(): Promise<void> {
    const companyId = this.tenant.companyId();
    if (!companyId) {
      this.softReset();
      return;
    }

    if (this.loadedCompanyIdSignal() === companyId && !this.errorSignal()) {
      return;
    }

    if (this.inflight) {
      await this.inflight;
      return;
    }

    this.loadingSignal.set(true);
    this.errorSignal.set(null);
    if (this.loadedCompanyIdSignal() !== companyId) {
      this.usersSignal.set([]);
    }

    this.inflight = this.fetchUsers()
      .then((list) => {
        this.usersSignal.set(list);
        this.loadedCompanyIdSignal.set(companyId);
        this.errorSignal.set(null);
      })
      .catch((err) => {
        this.usersSignal.set([]);
        this.loadedCompanyIdSignal.set(companyId);
        this.errorSignal.set(this.readError(err));
      })
      .finally(() => {
        this.loadingSignal.set(false);
        this.inflight = null;
      });

    await this.inflight;
  }

  /** Nome amigável para quick view / board; devolve o id se ainda não carregou ou não existe na lista. */
  resolveUserDisplayName(userId: string | null | undefined): string {
    const id = (userId ?? '').trim();
    if (!id) return '';
    return this.usersSignal().find((u) => u.id === id)?.name ?? id;
  }

  resolveUserIdByDisplayName(name: string | null | undefined): string | null {
    const n = (name ?? '').trim();
    if (!n) return null;
    const hit = this.usersSignal().find((u) => (u.name ?? '').trim() === n);
    return hit?.id ?? null;
  }

  resolveUserAvatarUrl(userId: string | null | undefined): string | null {
    const id = (userId ?? '').trim();
    if (!id) return null;
    const url = this.usersSignal().find((u) => u.id === id)?.avatarUrl;
    const s = (url ?? '').trim();
    return s.length ? s : null;
  }

  /** Invalida cache da empresa atual e recarrega. */
  async retry(): Promise<void> {
    const cid = this.tenant.companyId();
    if (!cid) return;
    this.loadedCompanyIdSignal.set(null);
    this.errorSignal.set(null);
    await this.ensureLoaded();
  }

  /** Limpa estado (troca de empresa / logout). */
  reset(): void {
    this.softReset();
  }

  private softReset(): void {
    this.usersSignal.set([]);
    this.errorSignal.set(null);
    this.loadedCompanyIdSignal.set(null);
    this.loadingSignal.set(false);
    this.inflight = null;
  }

  private async fetchUsers(): Promise<CompanyUserRef[]> {
    let raw: unknown;
    try {
      raw = await firstValueFrom(this.http.get<unknown>('/users'));
    } catch {
      raw = await firstValueFrom(this.http.get<unknown>('/api/users'));
    }
    return this.mapUsers(raw);
  }

  private mapUsers(raw: unknown): CompanyUserRef[] {
    const root = this.asRecord(raw);
    const source = this.resolveUsersSource(raw, root);
    const deduped = new Map<string, CompanyUserRef>();

    for (const entryRaw of source) {
      const entry = this.asRecord(entryRaw);
      const nestedUser = this.asRecord(entry['user'] ?? entry['User']);
      const src = Object.keys(nestedUser).length ? { ...entry, ...nestedUser } : entry;
      if (this.readIsDeletedFlag(src) || this.readIsDeletedFlag(entry)) continue;

      const actualUserId =
        this.asString(
          src['userId'] ??
            src['UserId'] ??
            src['applicationUserId'] ??
            src['ApplicationUserId'] ??
            entry['userId'] ??
            entry['UserId']
        ).trim() || '';
      const fallbackId =
        this.asString(src['id'] ?? src['Id'] ?? entry['id'] ?? entry['Id']).trim() || '';
      const id = actualUserId || fallbackId;
      if (!id) continue;

      const email =
        this.asString(src['email'] ?? src['Email']) ||
        this.asString(entry['email'] ?? entry['Email']);
      const name =
        this.asString(src['name'] ?? src['Name']) ||
        this.asString(src['fullName'] ?? src['FullName']) ||
        this.asString(src['displayName'] ?? src['DisplayName']) ||
        email;
      if (!name.trim()) continue;

      const avatarUrl =
        this.asString(
          src['avatarUrl'] ??
            src['AvatarUrl'] ??
            src['photoUrl'] ??
            src['PhotoUrl'] ??
            src['profileImageUrl'] ??
            src['ProfileImageUrl'] ??
            src['imageUrl'] ??
            src['ImageUrl']
        ) || null;

      if (!deduped.has(id)) {
        deduped.set(id, {
          id,
          name: name.trim(),
          email: email.trim(),
          avatarUrl: avatarUrl?.trim() || null,
        });
      }
    }

    return [...deduped.values()];
  }

  private resolveUsersSource(raw: unknown, root: Record<string, unknown>): unknown[] {
    if (Array.isArray(raw)) return raw;
    const items = this.asArray(root['items']);
    if (items.length) return items;
    const members = this.asArray(root['members']);
    const users = this.asArray(root['users']);
    const invites = this.asArray(root['invites']);
    const entries = this.asArray(root['entries']);
    return [...members, ...users, ...invites, ...entries];
  }

  private readIsDeletedFlag(rec: Record<string, unknown>): boolean {
    const v = rec['isDeleted'] ?? rec['IsDeleted'] ?? rec['is_deleted'];
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') return v.trim().toLowerCase() === 'true';
    if (typeof v === 'number') return v === 1;
    return false;
  }

  private readError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const payload = this.asRecord(error.error);
      const message = this.asString(payload['message']).trim();
      if (message) return message;
      return '__company_users_load_failed__';
    }
    return '__company_users_load_failed__';
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }
}
